import React, { useRef } from "react";
import { Divider, Typography } from "@mui/material";
import "react-medium-image-zoom/dist/styles.css";
import "../JaldiLab.css";
import {
  dimensionStyle,
  getTextContentHeading,
  h1style,
  h2style,
  h3style,
  paraStyle,
  referenceTootKit,
  sectionStyle,
  pointStyle,
  DashedBorderStyle,
  tableCaption,
  subHeadingContent,
} from "./ChapterList";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { tableCellClasses } from "@mui/material/TableCell";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";

const borderColor = "#AAAAAA";
const specialBorderColor = "#F3F3F4";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#F45B69",
    color: theme.palette.common.white,
    fontWeight: 700,
    fontSize: "16px",
    border: `1px solid ${borderColor}`,
    verticalAlign: "baseline",
  },
  [`&.${tableCellClasses.body}`]: {
    fontWeight: 400,
    fontSize: "14px",
    color: "#555555",
    border: `1px solid ${borderColor}`,
    verticalAlign: "baseline",
    lineHeight: "20px",
    textAlign: "left !important",
  },
  "&.bold-text": {
    fontWeight: 700,
  },
  "&:first-child": {
    borderLeft: `1px solid ${specialBorderColor}`,
  },
  "&:last-child": {
    borderRight: `1px solid ${specialBorderColor}`,
  },
  fontFamily: "Lato",
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: "#CCE5E5",
  },
}));

const LegalService = ({ goToSection }) => {
  const referencesRef = useRef(null);

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceHtml = (stringsArray) => {
    return (
      <ol>
        {stringsArray.map((text, index) => (
          <li style={{ ...pointStyle, paddingLeft: "10px" }} key={text.content}>
            {text.content}
            {text?.link && (
              <a
                style={{
                  color: "#DC002D",
                  textDecoration: "none",
                }}
                href={text.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {text.description}
              </a>
            )}
          </li>
        ))}
      </ol>
    );
  };

  const referenceData = [
    {
      content: "The Legal Service Authorities Act 1987, s. 12.",
    },
    {
      content: "Constitution of India 1950, art. 39A.",
    },
    {
      content: "The Legal Services Authorities Act 1987. ",
    },
  ];

  const legalServiceTable = [
    {
      section: "S.9.",
      sectionTitle: "District Legal Services Authority.",
      detail: (
        <>
          (2) A District Authority shall consist of—
          <div style={{ paddingLeft: "25px" }}>
            <p>(a) the District Judge who shall be its Chairman; and</p>
            <p>
              (b) such number of other members, possessing such experience and
              qualifications, as may be prescribed by the State Government, to
              be nominated by that Government in consultation with the Chief
              Justice of the High Court.
            </p>
          </div>
          <p>
            (3) The State Authority shall, in consultation with the Chairman of
            the District Authority, appoint a person belonging to the State
            Judicial Service not lower in rank than that of a Subordinate Judge
            or Civil Judge posted at the seat of the District Judiciary as
            Secretary of the District Authority to exercise such powers and
            perform such duties under the Chairman of that Committee as may be
            assigned to him by such Chairman.
          </p>
          <p>
            (5) The District Authority{" "}
            <b>
              may appoint such number of officers and other employees as may be
              prescribed by the State Government{" "}
            </b>
            in consultation with the Chief Justice of the High Court for the
            efficient discharge of its functions.
          </p>
        </>
      ),
      designConsider: (
        <>
          The Legal Services Authority must have its own office space with
          adequate space for the Chairman, and other members and staff as
          prescribed by the State Government.
          <p>
            The office of the Chairman may be designed similarly to the office
            space provided for the Judges of the Court (See:{" "}
            <span
              onClick={() =>
                goToSection("judgesChamber", "headingJudgeChamber")
              }
              style={sectionStyle}
            >
              {" "}
              Section 2.4.A
            </span>
            )
          </p>
        </>
      ),
    },
    {
      section: "S.10.",
      sectionTitle: "Functions of the District Authority.",
      detail: (
        <>
          (2) Without prejudice to the generality of the functions referred to
          in sub-section (1), the District Authority may perform all or any of
          the following functions, namely:—
          <div style={{ paddingLeft: "25px" }}>
            <p>
              (a) coordinate the activities of the Taluk Legal Services
              Committee and other legal services in the District;
            </p>
            <p>(b) organise Lok Adalats within the District; and</p>
            <p>
              (c) perform such other functions as the State Authority may fix by
              regulations.
            </p>
          </div>
        </>
      ),
      designConsider:
        "The DLSA may have adequate space within or in close proximity to its premises, for conducting Lok Adalats and legal awareness camps.",
    },
  ];

  const usersObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: [
      "Chairman",
      "Secretary",
      "Members",
      "Staff",
      "Advocates",
      "Litigants",
      "General Public",
    ],
    isOrdered: false,
    isImage: true,
  };

  const ObjectiveObject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Ensure that the space is
        <span style={{ color: "#DC002D" }}>
          {" "}
          accessible for diverse user groups{" "}
        </span>
        including Persons with Disabilities, elderly, expectant mothers,
        children, etc.;
      </>,

      <>
        Design spaces to
        <span style={{ color: "#DC002D" }}>
          {" "}
          encourage persons with limited resources to seek help{" "}
        </span>
        in their litigation processes;
      </>,

      <>
        Ensure that the spaces are
        <span style={{ color: "#DC002D" }}>
          {" "}
          non-intimidating, inviting, open, and accommodating{" "}
        </span>{" "}
        for all parties;
      </>,

      <>
        Ensure{" "}
        <span style={{ color: "#DC002D" }}>
          {" "}
          efficient functioning by providing adequate space{" "}
        </span>{" "}
        to accommodate all the functions of the DLSA;
      </>,

      <>
        Design spaces to
        <span style={{ color: "#DC002D" }}>
          {" "}
          enable confidential conversations{" "}
        </span>
        between the litigants and their Advocates, when necessary; and
      </>,

      <>
        Ensure that the spaces have
        <span style={{ color: "#DC002D" }}>
          {" "}
          adequate security provisions and fire safety measures
        </span>
        .
      </>,
    ],
    isOrdered: false,
    isImage: true,
  };

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "The DLSA may be its own self-sustained space within the Court Complex site. It must be in close proximity to the entrance of the Court Complex to ensure convenient access for litigants.",

      "In such instances where the DLSA is part of the Court Complex, it must be in close proximity to the entrance and to the public waiting areas. ",

      "It may be in close proximity to the e-Seva Kendra.",
    ],
    isOrdered: false,
    isImage: true,
  };

  const designConsiderationsObject = [
    {
      icon: <img src={designConsideration} />,
      heading: "Design Considerations",
      items: [
        <>
          The relevant provisions of the Legal Services Authorities Act, 1987
          which pertain to infrastructural requirements are:
          {reference(referenceData[2], 3)}
          <div style={{ marginTop: "30px" }}>
            <p className="table-guidance-heading">
              <span className="table-guidline-text">Table 13: </span>
              <br />
              <span style={tableCaption}>
                Design considerations arising from the Legal Services
                Authorities Act (1987).
              </span>
            </p>
            <TableContainer>
              <Table className="tableStyle">
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell>Section</StyledTableCell>
                    <StyledTableCell>Section Title</StyledTableCell>
                    <StyledTableCell>Details</StyledTableCell>
                    <StyledTableCell>Design Consideration</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {legalServiceTable &&
                    legalServiceTable.map((row, index) => (
                      <TableRow key={index}>
                        <StyledTableCell>{row.section}</StyledTableCell>
                        <StyledTableCell>{row.sectionTitle}</StyledTableCell>
                        <StyledTableCell>{row.detail}</StyledTableCell>
                        <StyledTableCell>{row.designConsider}</StyledTableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </>,
        "The DLSA must be clearly marked out by the use of accessible informational and directional signage.",

        <>
          It must have a reception area at the entrance to the DLSA premises, as
          well as adequate waiting areas for the litigants visiting the
          premises.
        </>,

        "Accessible information counters/kiosks/pamphlets must be provided, in local languages, for the public to get information regarding their rights. ",

        "An attached kitchen/pantry area, accessible and family-friendly toilets, and drinking water facilities must be provided. ",

        "All spaces within the DLSA must be accessible to diverse user groups, and must be equipped with ramps and elevators, accessible signage, tactile paths, etc.",

        "The space may be designed using natural elements, adequate windows and lighting, comfortable seating and indoor plants and greenery to ensure comfort for all users. ",
      ],
    },
    {
      heading: "Waiting Room",
      items: [
        "Waiting areas for litigants must have dedicated information kiosks providing information regarding the various cases that are being undertaken by the DLSA. ",

        <>
          Information screens providing accessible information regarding the
          rights of the citizens for legal aid, in local languages, must also be
          provided.For further details on the design of waiting areas, please
          refer to{" "}
          <span
            onClick={() => goToSection("waitingArea", "headingWaitingAreas")}
            style={sectionStyle}
          >
            {" "}
            Section 2.2.
          </span>
        </>,
      ],
    },
    {
      heading: "Reception Area",
      items: [
        "The DLSA  must have its own dedicated reception area with a counter.",

        "The reception must be manned by at least one receptionist for aiding litigants with proceedings referred for legal aid.",

        "The receptionist/information kiosks at the reception must be equipped to provide information to the litigants regarding their rights to legal aid, its benefits and information regarding the various events being undertaken by the Authority. ",
      ],
    },
    {
      heading: "Office Spaces",
      items: [
        <>
          A separate office space must be provided for the District Judge who is
          the ex-officio Chairman of the DLSA, designed in accordance with the
          design considerations highlighted in
          <span
            onClick={() => goToSection("judgesChamber", "headingJudgeChamber")}
            style={sectionStyle}
          >
            {" "}
            Section 2.4.A
          </span>
          .
        </>,

        <>
          Separate, comfortable workspaces must be provided for the DLSA
          members, staff, and empanelled Advocates. For further details on the
          design of single-person and multi-person office spaces, please refer
          to
          <span
            onClick={() =>
              goToSection("officeSpaces", "officeSpacesSingleWorkspace")
            }
            style={sectionStyle}
          >
            {" "}
            Section 2.5.B{" "}
          </span>
          and
          <span
            onClick={() =>
              goToSection("officeSpaces", "officeSpacesMultiPersonWorkspace")
            }
            style={sectionStyle}
          >
            {" "}
            2.5.C.
          </span>
        </>,

        "Workspaces for empanelled Advocates must additionally be equipped with provision for a secure, private meeting area with litigants. ",
        <>
          Separate meeting rooms must be provided to facilitate meetings between
          the empanelled Advocates and the litigants. The rooms should be
          acoustically treated to ensure privacy and confidentiality. For
          further details on acoustics, please refer to{" "}
          <span
            style={sectionStyle}
            onClick={() => goToSection("annexes", "HeadingAnnexure5")}
          >
            Annexure 5.
          </span>
        </>,
      ],
    },
  ];

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>
        <h2 id="HeadingChapterTwo" style={h2style}>
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANIZATION AND DESIGN OF SPACES OF THE COURT COMPLEX
        </h2>
        <h3 id="HeadingLegalService" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.8. </span>
          Para-Legal Facilities
        </h3>
        <h3 id="HeadingLegalService" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.8.</span>B. Legal Services
          Authority
        </h3>
        <p style={paraStyle}>
          Legal Aid Services are facilities provided in the District Courts
          through which state-funded legal aid is provided to persons “who have
          to file or defend a case”.{reference(referenceData[0], 1)} The
          Constitution of India mandates the provision of free legal aid to
          every citizen,{reference(referenceData[1], 2)} which has been
          understood as comprising a crucial part of the fundamental right to
          life and liberty under Articles 21 and 22. The Legal Services
          Authority Act, 1987 was instituted to put in place a structure for the
          execution of this provision.
        </p>
        <p style={paraStyle}>
          At the District Court level, every District Court is required to have
          District Legal Services Authorities (“DLSA”) for the dispensation of
          this function. The Centres provide free and competent legal
          counselling and advice, conduct Lok Adalats, free legal assistance to
          persons who qualify for the same under the Act, and undertake programs
          to increase the legal awareness among the common public.
        </p>
        <p style={paraStyle}>
          The DLSA offices must have adequate space to accommodate all of the
          above-mentioned functions. There must also be adequate space to ensure
          comfortable workspaces for the office-holders, staff and Advocates
          associated with the Centre.
        </p>
        {getTextContentHeading(ObjectiveObject)}
        {getTextContentHeading(usersObject)}
        {getTextContentHeading(adjacenciesObject)}
        {subHeadingContent(designConsiderationsObject)}

        <h2 ref={referencesRef} id="legalServiceRef">
          <i>References</i>
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
};

export default LegalService;
